import type {
  AdminCustomer,
  AdminCustomerHistoryResponse,
  AdminCustomerListResponse,
  AdminCustomerResponse,
  CreateAdminCustomerRequest,
  CustomerCategoryKey,
  UpdateAdminCustomerRequest,
} from '@mds/shared/types/customer';

import { api } from '@/lib/api';

/** The customer endpoints — see `backend/docs/customers.md`. */

/** A type alias, so it carries the index signature the client's `query` needs. */
export type CustomerQuery = {
  page?: number;
  limit?: number;
  /** Matched across reference, name, email, phone, tags and interests. */
  q?: string;
  category?: CustomerCategoryKey;
  tag?: string;
  sort?: 'reference' | 'name' | 'createdAt' | 'updatedAt';
  direction?: 'asc' | 'desc';
};

export const listCustomers = (query: CustomerQuery = {}) =>
  api.get<AdminCustomerListResponse>('admin/customers', { query });

export const getCustomer = (customerId: string): Promise<AdminCustomer> =>
  api.get<AdminCustomerResponse>(`admin/customers/${customerId}`).then((r) => r.data);

export const getCustomerHistory = (
  customerId: string,
  query: { page?: number; limit?: number } = {},
) => api.get<AdminCustomerHistoryResponse>(`admin/customers/${customerId}/history`, { query });

export const createCustomer = (body: CreateAdminCustomerRequest): Promise<AdminCustomer> =>
  api.post<AdminCustomerResponse>('admin/customers', body).then((r) => r.data);

/** PATCH takes only what changed, and rejects a body with nothing in it. */
export const updateCustomer = (
  customerId: string,
  body: UpdateAdminCustomerRequest,
): Promise<AdminCustomer> =>
  api.patch<AdminCustomerResponse>(`admin/customers/${customerId}`, body).then((r) => r.data);

/** Refused when bookings, event registrations, or proposals need the customer record. */
export const deleteCustomer = (customerId: string): Promise<null> =>
  api.delete<null>(`admin/customers/${customerId}`);
